import { ref, computed } from 'vue'
import { jsonapiClient } from '@/api/client'
import { isTextablePhone } from '@/lib/phone'
import type { paths } from '@/api/schema'
import { useAddressLookup, type AddressDraft, type PlaceAnswer, type PlaceCandidate } from '@/composables/useAddressLookup'

/**
 * Match-at-entry: the candidate moment's STATE MACHINE
 * (doc/FEATURE_CUSTOMER_DEDUP.md — the table is the design of record, approved
 * before code).
 *
 * ⭐ TWO questions, not one. A person match and a place match answer different
 * questions and can disagree (« this number is Ginette's » vs « this address is
 * Marc's »). They are independent axes here — answering one never answers the
 * other — and each renders as its own prompt in CustomerCreateBlock.
 *
 * The PLACE axis is the address lookup (useAddressLookup, design 170): which
 * house, then whether we already have it. The PERSON axis lives here.
 *
 * The instance is created by the PARENT form (which owns submit and the create
 * chain) and handed to the block as a prop — the block stays stateless, same as
 * its relationship to the vee-validate form.
 *
 * Laws (from the doc):
 *  - a raised question BLOCKS submit; a failed geocode does not (fail soft —
 *    that is the one path where an address enters unanswered, by design);
 *  - the REASON is always on screen — candidates carry their matched key;
 *  - no dismiss-as-confirmation: two named verbs, never an ✕.
 */

type MatchMeta =
  paths['/api/contact_matches']['get']['responses']['200']['content']['application/vnd.api+json']['meta']

export type { PlaceCandidate, PlaceAnswer }
export type PersonCandidate = MatchMeta['candidates'][number]

export type PersonState = 'idle' | 'candidate' | 'resolved'

// `release` is the cell-key « no »: under the claim a duplicate cell CANNOT be
// created, so refusing the match means taking the number from its holder —
// « Nouveau propriétaire — le retirer de sa fiche ». `new` is the email-key
// « no », where nothing is claimed and a plain duplicate person is allowed.
export type PersonAnswer =
  | { use: 'existing'; candidate: PersonCandidate }
  | { use: 'release'; candidate: PersonCandidate }
  | { use: 'new' }

export function useCustomerDedup(read: {
  address: () => AddressDraft | undefined
  cell: () => string
}) {
  const address = useAddressLookup(read.address, { placeQuestion: true })

  const personState = ref<PersonState>('idle')
  const personCandidate = ref<PersonCandidate | null>(null)
  const personAnswer = ref<PersonAnswer | null>(null)

  // Signature guard: blur fires on every focus hop, the network must not.
  let checkedCell = ''

  // The cell checks ON INPUT (debounced), not just blur — his call, overriding
  // the table's blur trigger: `isTextablePhone` already gates the fetch until
  // the number is complete, so this costs one request and warns the dispatcher
  // WHILE they are still in the field, not after they've moved on.
  let cellInputTimer: ReturnType<typeof setTimeout> | undefined
  const CELL_INPUT_DEBOUNCE_MS = 350

  const onCellInput = () => {
    clearTimeout(cellInputTimer)
    cellInputTimer = setTimeout(() => {
      void checkCell()
    }, CELL_INPUT_DEBOUNCE_MS)
  }

  const checkCell = async () => {
    const cell = read.cell().trim()
    if (cell === checkedCell) return
    checkedCell = cell

    personAnswer.value = null
    personCandidate.value = null
    if (!cell || !isTextablePhone(cell)) {
      // Nothing matchable — untextable input is the FIELD's error, not a
      // question to raise here.
      personState.value = 'idle'
      return
    }

    try {
      const response = await jsonapiClient.get('/api/contact_matches', {
        params: { cell },
      })
      if (cell !== checkedCell) return

      const candidate = response.data.meta.candidates[0] ?? null
      personCandidate.value = candidate
      personState.value = candidate ? 'candidate' : 'resolved'
    } catch {
      // Fail soft, the geocode rule's sibling: a match service outage must not
      // block intake. The claim index still backstops a true duplicate cell.
      if (cell !== checkedCell) return
      personState.value = 'resolved'
    }
  }

  const answerPerson = (answer: PersonAnswer) => {
    personAnswer.value = answer
    personState.value = 'resolved'
  }

  // Null-safe verb closures for the template — a handler closure can't carry a
  // v-if's narrowing, so the read happens here where the guard is.
  const acceptPerson = () => {
    const candidate = personCandidate.value
    if (candidate) answerPerson({ use: 'existing', candidate })
  }
  const releasePerson = () => {
    const candidate = personCandidate.value
    if (candidate) answerPerson({ use: 'release', candidate })
  }
  const reopenPerson = () => {
    if (!personCandidate.value) return
    personAnswer.value = null
    personState.value = 'candidate'
  }

  // The table's submit column: raised questions and the in-flight lookup
  // block; `failed` deliberately does NOT.
  const blocksSubmit = computed(() => address.blocksSave.value || personState.value === 'candidate')

  const chosenContactId = computed(() =>
    personAnswer.value?.use === 'existing' ? personAnswer.value.candidate.id : null,
  )
  const releaseCell = computed(() => personAnswer.value?.use === 'release')

  // The parents' § 55 wipe — a closed conversation starts clean.
  const reset = () => {
    clearTimeout(cellInputTimer)
    address.reset()
    personState.value = 'idle'
    personCandidate.value = null
    personAnswer.value = null
    checkedCell = ''
  }

  return {
    // The place axis, whole, for AddressLookupFields.
    address,
    placeState: address.state,
    placeCandidates: address.placeCandidates,
    placeAnswer: address.placeAnswer,
    house: address.house,
    coordinates: address.coordinates,
    checkAddress: address.check,
    ensureAnswered: address.ensureAnswered,
    answerPlace: address.answerPlace,
    reopenPlace: address.reopenPlace,
    chosenLocationId: address.chosenLocationId,
    // The person axis.
    personState, personCandidate, personAnswer,
    checkCell, onCellInput, answerPerson, acceptPerson, releasePerson, reopenPerson,
    blocksSubmit, chosenContactId, releaseCell,
    reset,
  }
}

export type CustomerDedup = ReturnType<typeof useCustomerDedup>
